export type PublicationOperationalSource = {
  id: string;
  title: string;

  summary:
    string |
    null;

  content:
    string;

  availableAt:
    string |
    null;

  publishedAt:
    string |
    null;

  capturedAt:
    string;

  lawyerName:
    string |
    null;

  linkedClientId:
    string |
    null;
};

export type PublicationCaseContext = {
  id: string;
  clientId: string;

  responsibleUser:
    string |
    null;
};

export type PublicationDeadlineLike = {
  sourceType:
    string;

  sourceId:
    string |
    null;

  status:
    string;
};

const toDateOnly =
  (
    value:
      string |
      null |
      undefined
  ) => {
    const text =
      String(
        value ??
        ''
      ).trim();

    if (!text) {
      return null;
    }

    const direct =
      text.slice(
        0,
        10
      );

    if (
      /^\d{4}-\d{2}-\d{2}$/.test(
        direct
      )
    ) {
      const [
        year,
        month,
        day,
      ] =
        direct
          .split(
            '-'
          )
          .map(
            Number
          );

      const parsed =
        new Date(
          Date.UTC(
            year,
            month - 1,
            day
          )
        );

      if (
        parsed.getUTCFullYear() ===
          year &&
        parsed.getUTCMonth() ===
          month - 1 &&
        parsed.getUTCDate() ===
          day
      ) {
        return direct;
      }
    }

    const timestamp =
      Date.parse(
        text
      );

    if (
      !Number.isFinite(
        timestamp
      )
    ) {
      return null;
    }

    return new Date(
      timestamp
    )
      .toISOString()
      .slice(
        0,
        10
      );
  };

export const getPublicationReferenceDate =
  (
    publication:
      PublicationOperationalSource
  ) =>
    toDateOnly(
      publication.availableAt
    ) ??
    toDateOnly(
      publication.publishedAt
    ) ??
    toDateOnly(
      publication.capturedAt
    );

const buildPublicationExcerpt =
  (
    publication:
      PublicationOperationalSource
  ) => {
    const source =
      (
        publication.summary ||
        publication.content ||
        ''
      ).trim();

    if (!source) {
      return null;
    }

    return source.slice(
      0,
      500
    );
  };

export const buildPublicationCaseDeadlineInput =
  (
    publication:
      PublicationOperationalSource,

    caseItem:
      PublicationCaseContext
  ) => {
    const referenceDate =
      getPublicationReferenceDate(
        publication
      );

    const excerpt =
      buildPublicationExcerpt(
        publication
      );

    const notes =
      [
        `Originado da publicação ${publication.id}.`,

        referenceDate
          ? `Data de referência da publicação: ${referenceDate}.`
          : null,

        'O marco inicial e a data final ainda precisam de conferência jurídica.',

        excerpt
          ? `Trecho para triagem: ${excerpt}`
          : null,
      ]
        .filter(
          Boolean
        )
        .join(
          '\n'
        );

    return {
      caseId:
        caseItem.id,

      legalDeadlineId:
        null,

      title:
        `Revisar prazo — ${publication.title}`,

      // Deliberadamente não inferidos.
      startDate:
        null,

      suggestedDueDate:
        null,

      dueDate:
        null,

      sourceType:
        'publication',

      sourceId:
        publication.id,

      status:
        'pending',

      riskLevel:
        'high',

      responsibleUser:
        caseItem.responsibleUser ??
        publication.lawyerName ??
        null,

      notes,

      createTask:
        false,
    };
  };

export const buildPublicationAnalysisTaskInput =
  (
    publication:
      PublicationOperationalSource,

    caseItem:
      PublicationCaseContext |
      null
  ) => {
    const referenceDate =
      getPublicationReferenceDate(
        publication
      );

    const excerpt =
      buildPublicationExcerpt(
        publication
      );

    return {
      title:
        `Analisar publicação — ${publication.title}`,

      description:
        excerpt,

      status:
        'pending',

      priority:
        'high',

      // Não é prazo processual.
      taskType:
        'follow_up',

      caseId:
        caseItem?.id ??
        null,

      clientId:
        caseItem?.clientId ??
        publication.linkedClientId ??
        null,

      responsibleUser:
        caseItem?.responsibleUser ??
        publication.lawyerName ??
        null,

      // Nunca usar a data da publicação como vencimento.
      dueDate:
        null,

      notes:
        [
          `Criada a partir da publicação ${publication.id}.`,

          referenceDate
            ? `Data de referência da publicação: ${referenceDate}.`
            : null,

          'Esta tarefa serve para análise da publicação e não representa prazo processual confirmado.',
        ]
          .filter(
            Boolean
          )
          .join(
            '\n'
          ),
    };
  };

export const findExistingPublicationDeadline =
  (
    deadlines:
      PublicationDeadlineLike[],

    publicationId:
      string
  ) =>
    deadlines.find(
      (
        deadline
      ) =>
        deadline.sourceType ===
          'publication' &&
        deadline.sourceId ===
          publicationId &&
        deadline.status !==
          'archived'
    ) ??
    null;